import i18n from '@/i18n';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { guildRosterKey } from '@/lib/useGuildRoster';
import type { GuildBan } from '@/types/api';

/**
 * Guild ban list + ban/unban for a guild. Powers the "Заблокировать на
 * сервере" action in the user card and the "забанен" treatment in the
 * message list. Banning also invalidates the roster (the member is kicked).
 *
 * Listing bans is moderator-gated server-side, so pass `enabled: false` for
 * non-moderators to avoid a guaranteed 403.
 */
export function bansKey(guildId: string) {
  return ['guild', guildId, 'bans'] as const;
}

export interface UseGuildBans {
  bans: GuildBan[];
  bannedIds: Set<string>;
  isBanned: (userId: string) => boolean;
  ban: (userId: string) => void;
  unban: (userId: string) => void;
  pendingId: string | null;
}

export function useGuildBans(
  guildId: string | null | undefined,
  opts?: { enabled?: boolean },
): UseGuildBans {
  const qc = useQueryClient();
  const enabled = !!guildId && (opts?.enabled ?? true);

  const query = useQuery({
    queryKey: bansKey(guildId ?? '∅'),
    queryFn: () => api.listBans(guildId as string),
    enabled,
  });

  const invalidate = () => {
    if (!guildId) return;
    void qc.invalidateQueries({ queryKey: bansKey(guildId) });
    // The banned member is kicked — refresh the roster too.
    void qc.invalidateQueries({ queryKey: guildRosterKey(guildId) });
  };

  const banM = useMutation({
    mutationFn: (userId: string) => api.banMember(guildId as string, userId),
    onSuccess: invalidate,
    onError: (err) => toast.error(...errToToast(err, i18n.t('lib2.bans.banFailed'))),
  });
  const unbanM = useMutation({
    mutationFn: (userId: string) => api.unbanMember(guildId as string, userId),
    onSuccess: invalidate,
    onError: (err) => toast.error(...errToToast(err, i18n.t('lib2.bans.unbanFailed'))),
  });

  const bans = query.data?.bans ?? [];
  const bannedIds = new Set(bans.map((b) => b.userId));
  const pendingId =
    (banM.isPending ? banM.variables ?? null : null) ??
    (unbanM.isPending ? unbanM.variables ?? null : null);

  return {
    bans,
    bannedIds,
    isBanned: (userId: string) => bannedIds.has(userId),
    ban: (userId: string) => banM.mutate(userId),
    unban: (userId: string) => unbanM.mutate(userId),
    pendingId,
  };
}
